import { Person, WithContext } from 'schema-dts';

import { Locale } from '../../../../core/i18n/Locale';
import { Site } from '../../../../core/Site/Site';
import { Author } from '../../../../resources/author/domain/types/author';
import { htmlToText } from '../../../../shared/utilities/htmlToText';
import { generateUrl } from '../../../../shared/utilities/routing/routing';
import { PickDomain } from '../../../../shared/utilities/types/PickDomain';
import { imageUrlForCdn } from '../../Image/URL/ImageURL';

export function getAuthorJsonLd({
    author,
    locale,
    currentSite,
}: {
    author: PickDomain<
        Author,
        'id' | 'firstName' | 'lastName' | 'role' | 'description' | 'picture' | 'slug',
        {
            instagramUrl: Author['instagramUrl'] | null;
            linkedinUrl: Author['linkedinUrl'] | null;
        }
    >;
    locale: Locale;
    currentSite: Site;
}): WithContext<Person> {
    const imageWidth = 800;
    const imageHeight = 450;
    const authorPageUrl = generateUrl(
        'author',
        locale,
        { authorId: author.id, authorSlug: author.slug },
        true,
    );
    const authorImage = {
        '@type': 'ImageObject' as const,
        url: imageUrlForCdn(author.picture, imageWidth, imageHeight),
        caption: author.picture.legend,
        width: {
            '@type': 'QuantitativeValue' as const,
            value: imageWidth,
        },
        height: {
            '@type': 'QuantitativeValue' as const,
            value: imageHeight,
        },
    };

    return {
        '@context': 'https://schema.org',
        '@type': 'Person',
        name: `${author.firstName} ${author.lastName}`,
        givenName: author.firstName,
        familyName: author.lastName,
        jobTitle: author.role,
        description: htmlToText(author.description),
        image: authorImage,
        url: authorPageUrl,
        worksFor: {
            '@type': 'Organization',
            name: currentSite.organisation.name,
            url: currentSite.organisation.url,
        },
        sameAs: [
            author.linkedinUrl ? author.linkedinUrl : null,
            author.instagramUrl ? author.instagramUrl : null,
        ].filter(Boolean) as string[],
    };
}
